import React, { useEffect, useMemo, useRef, useState, VFC } from 'react';
import { RadioGroup, RadioGroupItem } from 'src/common/form/radio-group';
import {
  CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY,
  CONTENT_BEHAVIOR_EXTERNAL_LINK,
  CONTENT_TYPE_EXTERNAL_LINK,
  CONTENT_TYPE_FILE,
  CONTENT_TYPE_PDF,
  STEP_EDITOR_CONTENT_OPTION_EXISTING_CONTENT,
  STEP_EDITOR_CONTENT_OPTION_RECORD,
  STEP_EDITOR_CONTENT_OPTION_UPLOAD,
} from 'src/editor/constants';
import { ContentBehavior, useStepEditorContext } from 'src/store/step-editor';
import { StepEditorContentBehaviorExternalLinkOptions } from './external-link-options';
import { useEmbedUrlChange } from '../../use-embed-url-change.hook';
import { Nullable } from 'src/types/nullable.type';
import { ToggleComponent } from 'src/common/form/toggle/component';
import { apiUpdateNodeDownloadable } from 'src/utils/journeyApi';

interface StepEditorContentBehaviorPanelProps {
  value: string;
  defaultOpen?: boolean;
  downloadable: boolean;
}

export const DefaultContentBehaviorOptions: Array<RadioGroupItem<ContentBehavior>> = [
  {
    value: CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY,
    label: 'Display in Journey',
  },
  {
    label: 'Link off to content',
    value: CONTENT_BEHAVIOR_EXTERNAL_LINK,
  },
];

export const StepEditorContentBehaviorPanel: VFC<StepEditorContentBehaviorPanelProps> = ({ value, downloadable }) => {
  const {
    url,
    node_id,
    contentType,
    contentOption,
    contentBehavior,
    oldContentBehavior,
    setData,
    setOldContentBehavior,
    link,
    setLink,
    isEmbeddable,
    setContentType,
    setContentBehavior,
    resetOldContentBehavior,
  } = useStepEditorContext();
  const [disableDownloadToggle, setDisableDownloadToggle] = useState(false);
  const initialContentBehaviorRef = useRef(contentBehavior);
  const { checkEmbedUrl } = useEmbedUrlChange({ overwriteLink: false });
  const [prevValue, setPrevValue] = useState<Nullable<string>>(null);

  const isContentExternalFile = contentType === CONTENT_TYPE_FILE;
  const isContentPDF = contentType === CONTENT_TYPE_PDF;
  const isContentBehaviorExternal = value === CONTENT_BEHAVIOR_EXTERNAL_LINK;

  const onContentBehaviorChanged = (option: RadioGroupItem<ContentBehavior>) => {
    if (option.value !== contentBehavior) {
      setContentBehavior(option.value);
    }
    setContentBehavior(option.value);
    if (option.value === CONTENT_BEHAVIOR_EXTERNAL_LINK) {
      setContentType(CONTENT_TYPE_EXTERNAL_LINK);
    } else if (option.value === CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY) {
      checkEmbedUrl(url, { forceCheck: true });
    }
  };

  useEffect(() => {
    if (!prevValue && !isContentExternalFile) {
      setPrevValue(value);
    }

    if (!isContentBehaviorExternal && oldContentBehavior !== value) {
      resetOldContentBehavior();
    }

    setOldContentBehavior(value);
  }, [
    value,
    link,
    node_id,
    resetOldContentBehavior,
    setOldContentBehavior,
    oldContentBehavior,
    prevValue,
    isContentBehaviorExternal,
    isContentExternalFile,
  ]);

  const contentBehaviorOptions = useMemo(() => {
    const behaviorOptions = [...DefaultContentBehaviorOptions];
    if (!isEmbeddable && initialContentBehaviorRef.current !== CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY) {
      behaviorOptions[0].disabled = true;
    }
    behaviorOptions[1].disabled =
      contentOption === STEP_EDITOR_CONTENT_OPTION_EXISTING_CONTENT ||
      contentOption === STEP_EDITOR_CONTENT_OPTION_UPLOAD ||
      contentOption === STEP_EDITOR_CONTENT_OPTION_RECORD;

    return behaviorOptions;
  }, [contentOption, isEmbeddable]);

  const onChangeAllowDownloadToggle = async (checked: boolean) => {
    if (!isContentPDF) {
      return;
    }
    if (node_id) {
      setDisableDownloadToggle(true);
      const { downloadable } = await apiUpdateNodeDownloadable(node_id, checked);
      setData({ downloadable });
      setDisableDownloadToggle(false);
    }
  };

  return (
    <div className='flex flex-col space-y-lg'>
      <h1 className='heading-bedrock-tertiary'>Content behavior</h1>
      {contentType !== CONTENT_TYPE_FILE && (
        <div className='flex flex-col space-y-sm'>
          <span className='text-bedrock-p-strong text-bedrock-black'>How should this appear</span>
          <RadioGroup
            value={value}
            onChange={onContentBehaviorChanged}
            name='content_behavior'
            options={contentBehaviorOptions}
          />
        </div>
      )}
      {isContentPDF && (
        <ToggleComponent
          checked={downloadable}
          onChange={onChangeAllowDownloadToggle}
          disabled={disableDownloadToggle}
          label='Allow download'
        />
      )}
      {(isContentExternalFile || isContentBehaviorExternal) && (
        <StepEditorContentBehaviorExternalLinkOptions
          linkTitle={link.title}
          linkDescription={link.description}
          hideThumbnail={link.hide_image}
          setLinkTitle={(title) => setLink({ ...link, title })}
          setLinkDescription={(description) => setLink({ ...link, description })}
          setHideThumbnail={(hideThumbnail) => setLink({ ...link, hide_image: hideThumbnail })}
        />
      )}
    </div>
  );
};
